import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api, money } from "@/lib/apiClient";
import { PageHeader, StatusBadge, EmptyState } from "@/components/Common";
import { ComplianceNote } from "@/components/AppShell";
import { FileText, TrendingUp, Wallet, CheckCircle2, Plus, AlertTriangle } from "lucide-react";

const Metric = ({ label, value, icon: Icon, hint }) => (
  <div className="bg-white border border-slate-200 shadow-sm rounded-md p-5 hover:shadow-md transition-shadow">
    <div className="flex items-center justify-between">
      <p className="text-xs font-medium uppercase tracking-wide text-slate-500">{label}</p>
      <Icon size={18} className="text-blue-600" />
    </div>
    <p className="text-3xl font-bold tracking-tight text-slate-900 mt-3"
      style={{ fontFamily: "Manrope, sans-serif" }}>{value}</p>
    {hint && <p className="text-xs text-slate-400 mt-1.5">{hint}</p>}
  </div>
);

export default function Dashboard() {
  const [stats, setStats] = useState(null);
  const [quotes, setQuotes] = useState([]);
  const navigate = useNavigate();

  useEffect(() => {
    (async () => {
      const [s, q] = await Promise.all([api.get("/dashboard/stats"), api.get("/quotes")]);
      setStats(s.data);
      setQuotes(q.data.slice(0, 6));
    })().catch(() => {});
  }, []);

  return (
    <div data-testid="dashboard-page">
      <PageHeader title="Dashboard" subtitle="Pipeline, acceptance and compliance at a glance">
        <button data-testid="new-quote-btn" onClick={() => navigate("/quotes/new")}
          className="inline-flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white rounded-md px-4 py-2 text-sm font-medium transition-colors active:scale-95">
          <Plus size={16} /> New Document
        </button>
      </PageHeader>

      {stats && !stats.abn_set && (
        <Link to="/settings" data-testid="abn-warning-banner"
          className="flex items-start gap-2.5 rounded-md border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700 mb-6 hover:bg-red-100 transition-colors">
          <AlertTriangle size={16} className="mt-0.5 shrink-0" />
          <span>Add your ABN in Business Profile to unlock compliant tax invoicing.</span>
        </Link>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6">
        <Metric label="Open Quotes" value={stats?.open_quotes ?? "–"} icon={FileText} hint="Draft, sent or viewed" />
        <Metric label="Pipeline Value" value={stats ? money(stats.pipeline_value) : "–"} icon={Wallet} hint="Inc. GST" />
        <Metric label="Acceptance Rate" value={stats ? `${stats.acceptance_rate}%` : "–"} icon={TrendingUp} hint="Accepted vs decided" />
        <Metric label="Accepted Value" value={stats ? money(stats.accepted_value) : "–"} icon={CheckCircle2} hint="Ready to invoice" />
      </div>

      <div className="mt-8 grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 bg-white border border-slate-200 shadow-sm rounded-md overflow-hidden">
          <div className="flex items-center justify-between px-5 py-4 border-b border-slate-200">
            <h2 className="font-semibold text-slate-900">Recent documents</h2>
            <Link to="/quotes" className="text-xs font-medium text-blue-600 hover:text-blue-700">View all</Link>
          </div>
          {quotes.length === 0 ? (
            <div className="p-5">
              <EmptyState icon={FileText} title="No quotes yet"
                description="Create your first quote or estimate to start tracking your pipeline."
                action={<Link to="/quotes/new" data-testid="empty-new-quote-link"
                  className="text-sm font-medium text-blue-600">Create a document →</Link>} />
            </div>
          ) : (
            <table className="w-full text-sm" data-testid="recent-quotes-table">
              <thead className="bg-slate-50 border-b border-slate-200 text-slate-600 text-xs uppercase tracking-wider">
                <tr>
                  <th className="text-left font-semibold px-5 py-2.5">Number</th>
                  <th className="text-left font-semibold px-5 py-2.5 hidden sm:table-cell">Client</th>
                  <th className="text-left font-semibold px-5 py-2.5">Status</th>
                  <th className="text-right font-semibold px-5 py-2.5">Total</th>
                </tr>
              </thead>
              <tbody>
                {quotes.map((q) => (
                  <tr key={q.id} onClick={() => navigate(`/quotes/${q.id}`)}
                    data-testid={`dashboard-quote-row-${q.number}`}
                    className="border-b border-slate-100 last:border-0 hover:bg-slate-50 transition-colors cursor-pointer">
                    <td className="px-5 py-3">
                      <span className="font-semibold text-slate-900">{q.number}</span>
                      <span className="ml-2 text-[10px] uppercase tracking-wide text-slate-400">{q.doc_type}</span>
                    </td>
                    <td className="px-5 py-3 text-slate-600 hidden sm:table-cell">{q.client_name || "—"}</td>
                    <td className="px-5 py-3"><StatusBadge status={q.status} /></td>
                    <td className="px-5 py-3 text-right font-semibold text-slate-900">{money(q.totals.total_inc_gst)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        <div className="space-y-4">
          <div className="bg-white border border-slate-200 shadow-sm rounded-md p-5">
            <h2 className="font-semibold text-slate-900 mb-4">By status</h2>
            <div className="space-y-2.5">
              {stats && Object.entries(stats.by_status).map(([k, v]) => (
                <div key={k} className="flex items-center justify-between">
                  <StatusBadge status={k} />
                  <span className="text-sm font-semibold text-slate-700" data-testid={`status-count-${k}`}>{v}</span>
                </div>
              ))}
            </div>
          </div>
          <div className="bg-white border border-slate-200 shadow-sm rounded-md p-5 flex items-center justify-between">
            <div>
              <p className="text-xs uppercase tracking-wide text-slate-500">Clients</p>
              <p className="text-2xl font-bold text-slate-900 mt-1">{stats?.clients ?? "–"}</p>
            </div>
            <div>
              <p className="text-xs uppercase tracking-wide text-slate-500">Catalog</p>
              <p className="text-2xl font-bold text-slate-900 mt-1">{stats?.catalog_items ?? "–"}</p>
            </div>
          </div>
          <ComplianceNote />
        </div>
      </div>
    </div>
  );
}
